
import React from "react";
import { formatText } from "@/utils/report";

type TextParagraphProps = {
  text: string;
  className?: string;
  isExecutiveSummary?: boolean;
};

/**
 * Renders a paragraph of text with consistent formatting.
 * All text is displayed in normal weight (no bold) for consistency.
 */
const TextParagraph = ({
  text,
  className = "",
  isExecutiveSummary = false,
}: TextParagraphProps) => {
  // Check if this is an Introduction & Methodology section
  const isIntroductionSection =
    text.toLowerCase().includes("introduction") ||
    text.toLowerCase().includes("methodology");

  // Check if this is a References section - improved detection
  const isReferencesSection =
    text.toLowerCase().includes("references") &&
    (text.includes("http") || /\(20\d{2}\)/.test(text) || /\[\d+\]/.test(text));

  // Check if this is an error message - expanded detection
  const isErrorMessage =
    text.toLowerCase().includes("error") ||
    text.toLowerCase().includes("failed to") ||
    text.toLowerCase().includes("couldn't") ||
    text.toLowerCase().includes("apologize") ||
    text.toLowerCase().includes("try again") ||
    text.toLowerCase().includes("limit") ||
    text.toLowerCase().includes("exceeded") ||
    text.toLowerCase().includes("i'm sorry") ||
    text.toLowerCase().includes("you've reached") ||
    text.toLowerCase().includes("please subscribe") ||
    text.toLowerCase().includes("unavailable");

  // Skip references section entirely - the dedicated ReportReferences component will handle this
  if (isReferencesSection) {
    return null;
  }

  // Handle placeholder content consistently - remove brackets
  if (
    text.includes("XYZ:") ||
    text.includes("ABC:") ||
    text.includes("Company XYZ") ||
    text.includes("Company ABC")
  ) {
    return (
      <div className="text-base text-gray-400 italic mb-4 px-4 py-2 bg-gray-800/20 rounded">
        N/A - Specific competitor information not available
      </div>
    );
  }

  // Process text consistently regardless of type - thoroughly remove all formatting
  const processText = (inputText: string) => {
    // Remove all formatting completely (*, #, HTML tags)
    let processed = inputText
      .replace(/[*#]/g, "") // Remove all * and # characters
      .replace(/<strong>([^<]+)<\/strong>/g, "$1") // Remove HTML strong tags
      .replace(/<b>([^<]+)<\/b>/g, "$1") // Remove HTML b tags
      .replace(/<em>([^<]+)<\/em>/g, "$1") // Remove HTML em tags
      .replace(/<i>([^<]+)<\/i>/g, "$1"); // Remove HTML i tags

    // Process citations to make them consistent and clickable
    processed = processed.replace(
      /\[(\d+)\]/g,
      "<sup><a href='#reference-$1' class='text-blue-400 hover:text-blue-300'>[$1]</a></sup>"
    );

    // Clean up any remaining formatting
    return processed
      .replace(/^#+\s*/gm, "") // Remove markdown headings
      .replace(/\[([^\]]+)\]/g, "$1") // Remove brackets around placeholder text
      .replace(/(\d+)\s+percent/gi, "$1%") // Convert "XX percent" to "XX%"
      .replace(/^\*\s*/gm, "") // Remove leading asterisks at line start
      .replace(/\n{3,}/g, "\n\n"); // Normalize paragraph spacing
  };

  // All paragraphs should have the same base styling with consistent font weight
  // Note the explicit font-normal class to ensure normal weight (not bold)
  const baseStyles = "text-base text-gray-300 mb-4 leading-relaxed font-normal";

  // For error messages, ensure they're displayed with appropriate color
  if (isErrorMessage) {
    return (
      <p
        className={`${baseStyles} text-red-400 ${className}`}
        dangerouslySetInnerHTML={{
          __html: processText(text),
        }}
      />
    );
  }

  // For Introduction & Methodology sections, ensure they're never bold
  if (isIntroductionSection) {
    return (
      <p
        className={`${baseStyles} ${className} font-normal`}
        dangerouslySetInnerHTML={{
          __html: processText(text),
        }}
      />
    );
  }

  // All paragraphs use exactly the same styling with font-normal 
  // to ensure no bold text appears anywhere
  return (
    <p
      className={`${baseStyles} ${className}`}
      dangerouslySetInnerHTML={{
        __html: processText(text),
      }}
    />
  );
};

export default TextParagraph;
